import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { User, Menu, X, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuthContext } from '../../App';
import { useAccessControl } from '../../context/AccessControlContext';
import userService from '../../services/userServices';
import billingServices from '../../services/billingServices';
import { showSuccessBanner, showErrorBanner } from '../../components/common/ErrorBannerManager';
import styles from './OnboardingUserInfo.module.css';
import FoundryLogo from '../../components/assets/Foundry_logo_dark_transparent_background.png';
import FoundryLogoLight from '../../components/assets/Foundry_logo_light_transparent_background.png';

/**
 * Input sanitization utilities to prevent XSS and SQL injection
 */
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  // Remove or escape potentially dangerous characters
  return input
    // Remove script tags and their content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove other HTML tags
    .replace(/<[^>]*>/g, '')
    // Escape SQL injection patterns
    .replace(/['"\\;]/g, (match) => {
      switch (match) {
        case "'": return "''";
        case '"': return '""';
        case '\\': return '\\\\';
        case ';': return '';
        default: return match;
      }
    })
    // Remove SQL keywords that could be used in injection
    .replace(/\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|ALTER|CREATE|TRUNCATE|EXEC|EXECUTE)\b/gi, '')
    // Remove null bytes
    .replace(/\0/g, '')
    // Trim whitespace
    .trim();
};

/**
 * Validate input doesn't contain malicious patterns
 */
const validateInputSafety = (input, fieldName) => {
  if (typeof input !== 'string') return null;
  
  // Check for SQL injection patterns
  const sqlPatterns = [
    /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|ALTER|CREATE|TRUNCATE)\b)/i,
    /(--)|(\/\*)|(\*\/)/,
    /(\bOR\b\s+\d+\s*=\s*\d+)/i,
    /(\bAND\b\s+\d+\s*=\s*\d+)/i,
    /(\'\s*OR\s*\')/i,
  ];
  
  for (const pattern of sqlPatterns) {
    if (pattern.test(input)) {
      return `${fieldName} contains invalid characters`;
    }
  }
  
  // Check for XSS patterns
  const xssPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i, // onclick=, onerror=, etc.
    /<iframe/i,
    /<object/i,
    /<embed/i,
  ];
  
  for (const pattern of xssPatterns) {
    if (pattern.test(input)) {
      return `${fieldName} contains invalid content`;
    }
  }
  
  return null;
};

/**
 * Validate field length
 */
const validateFieldLength = (input, fieldName, maxLength) => {
  if (typeof input === 'string' && input.length > maxLength) {
    return `${fieldName} must be ${maxLength} characters or less`;
  }
  return null;
};

const OnboardingUserInfo = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { setIsAuthenticated } = useAuthContext();
  const { refresh: refreshAccessControl } = useAccessControl();
  
  // Form state
  // Note: consentToNewsletter defaults to true - users opt out via unsubscribe link in emails
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    jobTitle: '',
    designProblems: '',
    currentTools: '',
    howHeardAboutUs: '',
    agreeToTerms: false,
    consentToNewsletter: true  // Default opt-in
  });
  
  const [errors, setErrors] = useState({});
  const [hasVerified, setHasVerified] = useState(false);
  
  useEffect(() => {
    // Prevent multiple executions
    if (hasVerified) return;
    
    // Check if coming from successful checkout
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    const planId = searchParams.get('plan_id');
    
    if (success === 'true') {
      setHasVerified(true);
      
      // Verify the checkout session if we have a session_id
      if (sessionId) {
        const verifySub = async () => {
          try {
            await billingServices.verifyCheckoutSession(sessionId);
            
            // Refresh access control to update user permissions immediately
            // This prevents the "stale state" issue where users can't access features
            // until they sign out and back in
            await refreshAccessControl();
            
            showSuccessBanner({
              title: 'Subscription Activated!',
              message: planId ? `Your ${planId} plan is now active.` : 'Your subscription is now active.',
              position: 'top-right'
            });
          } catch (err) {
            console.error('Failed to verify checkout session:', err);
            // Still refresh access control - webhook may have already processed
            await refreshAccessControl();
            // Still show success - webhook will handle it
            showSuccessBanner({
              title: 'Subscription Activating',
              message: 'Your subscription is being activated.',
              position: 'top-right'
            });
          }
        };
        verifySub();
      } else {
        // No session_id but success=true, refresh access control anyway
        refreshAccessControl();
        showSuccessBanner({
          title: 'Subscription Activated!',
          message: planId ? `Your ${planId} plan is now active.` : 'Your subscription is now active.',
          position: 'top-right'
        });
      }
    } else if (!success) {
      // If not coming from successful checkout, redirect to billing
      showErrorBanner({
        title: 'Checkout Required',
        message: 'Please complete the checkout process first.',
        position: 'top-right'
      });
      navigate('/onboarding/billing', { replace: true });
    }
  }, [navigate, searchParams, hasVerified, refreshAccessControl]);
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Required field validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.company.trim()) {
      newErrors.company = 'Company is required';
    }
    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    
    // Field length validation
    const lengthError1 = validateFieldLength(formData.name, 'Name', 255);
    if (lengthError1) newErrors.name = lengthError1;
    
    const lengthError2 = validateFieldLength(formData.company, 'Company', 255);
    if (lengthError2) newErrors.company = lengthError2;
    
    const lengthError3 = validateFieldLength(formData.jobTitle, 'Job title', 255);
    if (lengthError3) newErrors.jobTitle = lengthError3;
    
    const lengthError4 = validateFieldLength(formData.howHeardAboutUs, 'How heard about us', 255);
    if (lengthError4) newErrors.howHeardAboutUs = lengthError4;
    
    const lengthError5 = validateFieldLength(formData.designProblems, 'Design problems', 2000);
    if (lengthError5) newErrors.designProblems = lengthError5;
    
    const lengthError6 = validateFieldLength(formData.currentTools, 'Current tools', 2000);
    if (lengthError6) newErrors.currentTools = lengthError6;
    
    // Security validation (SQL injection, XSS)
    const safetyError1 = validateInputSafety(formData.name, 'Name');
    if (safetyError1 && !newErrors.name) newErrors.name = safetyError1;
    
    const safetyError2 = validateInputSafety(formData.company, 'Company');
    if (safetyError2 && !newErrors.company) newErrors.company = safetyError2;
    
    const safetyError3 = validateInputSafety(formData.jobTitle, 'Job title');
    if (safetyError3 && !newErrors.jobTitle) newErrors.jobTitle = safetyError3;
    
    const safetyError4 = validateInputSafety(formData.designProblems, 'Design problems');
    if (safetyError4 && !newErrors.designProblems) newErrors.designProblems = safetyError4;
    
    const safetyError5 = validateInputSafety(formData.currentTools, 'Current tools');
    if (safetyError5 && !newErrors.currentTools) newErrors.currentTools = safetyError5;
    
    const safetyError6 = validateInputSafety(formData.howHeardAboutUs, 'How heard about us');
    if (safetyError6 && !newErrors.howHeardAboutUs) newErrors.howHeardAboutUs = safetyError6;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Sanitize all inputs before sending to backend
      const sanitizedData = {
        name: sanitizeInput(formData.name),
        company: sanitizeInput(formData.company),
        job_title: sanitizeInput(formData.jobTitle),
        design_problems: sanitizeInput(formData.designProblems),
        current_tools: sanitizeInput(formData.currentTools),
        how_heard_about_us: sanitizeInput(formData.howHeardAboutUs),
        consent_to_newsletter: formData.consentToNewsletter
      };
      
      // Save user info to backend
      await userService.updateUserInfo(sanitizedData);
      
      // Final refresh of access control before redirecting to landing
      // This ensures user has full permissions when they land on the main page
      await refreshAccessControl();
      
      showSuccessBanner({
        title: 'Welcome to Foundry!',
        message: 'Your profile has been saved. Redirecting to your landing page.',
        position: 'top-right'
      });
      
      // Redirect to landing page after a short delay
      setTimeout(() => {
        navigate('/landing', { replace: true });
      }, 1500);
    } catch (err) {
      console.error('Failed to save user info:', err);
      showErrorBanner({
        title: 'Error',
        message: 'Failed to save your information. Please try again.',
        position: 'top-right'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleLogout = async () => {
    setIsAuthenticated(false);
    try {
      await userService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
    navigate('/login', { replace: true });
  };
  
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  
  return (
    <>
      {/* TaskBar */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.logo}>
            <Link to="/landing" className={styles.logoLink}>
              <img 
                src={theme === 'dark' ? FoundryLogo : FoundryLogoLight} 
                alt="FOUNDRY" 
                className={styles.logoImage} 
              />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <nav className={styles.nav}>
            {/* Theme Toggle Button */}
            <button 
              onClick={toggleTheme}
              className={styles.themeToggle}
              aria-label="Toggle theme"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            
            <button onClick={handleLogout} className={styles.logoutButton}>Log Out</button>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className={styles.mobileMenuButton}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className={styles.mobileMenu}>
            {/* Theme Toggle for Mobile */}
            <button 
              onClick={() => {
                toggleTheme();
                toggleMobileMenu();
              }}
              className={styles.mobileThemeToggle}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <>
                  <Sun size={18} />
                  <span>Light Mode</span>
                </>
              ) : (
                <>
                  <Moon size={18} />
                  <span>Dark Mode</span>
                </>
              )}
            </button>
            
            <button onClick={() => { handleLogout(); toggleMobileMenu(); }} className={styles.mobileLogoutButton}>
              Log Out
            </button>
          </div>
        )}
      </header>
    
      <div className={styles.onboardingPage}>
        <div className={styles.container}>
          {/* Header */}
          <div className={styles.pageHeader}>
            <h1 className={styles.title}>Tell Us About Yourself</h1>
            <p className={styles.subtitle}>Help us personalize your experience</p>
          </div>
        
          {/* User Info Form Card */}
          <div className={styles.formCard}>
            <div className={styles.formHeader}>
              <User className={styles.formIcon} size={28} />
              <h2 className={styles.formTitle}>Your Information</h2>
            </div>
            
            <form onSubmit={handleSubmit} className={styles.form}>
              {/* Required Fields */}
              <div className={styles.formGroup}>
                <label htmlFor="name" className={styles.label}>
                  Name <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                  placeholder="Your full name"
                />
                {errors.name && <span className={styles.errorText}>{errors.name}</span>}
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="company" className={styles.label}>
                  Company <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className={`${styles.input} ${errors.company ? styles.inputError : ''}`}
                  placeholder="Your company name"
                />
                {errors.company && <span className={styles.errorText}>{errors.company}</span>}
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="jobTitle" className={styles.label}>
                  Job Title <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="jobTitle"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleInputChange}
                  className={`${styles.input} ${errors.jobTitle ? styles.inputError : ''}`}
                  placeholder="Your job title"
                />
                {errors.jobTitle && <span className={styles.errorText}>{errors.jobTitle}</span>}
              </div>
              
              {/* Optional Fields */}
              <div className={styles.formGroup}>
                <label htmlFor="designProblems" className={styles.label}>
                  What design, rendering or simulation problems are you trying to solve?
                </label>
                <textarea
                  id="designProblems"
                  name="designProblems"
                  value={formData.designProblems}
                  onChange={handleInputChange}
                  className={styles.textarea}
                  placeholder="Tell us about the challenges you're facing..."
                  rows={3}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="currentTools" className={styles.label}>
                  Which tools do you currently use to solve these problems?
                </label>
                <textarea
                  id="currentTools"
                  name="currentTools"
                  value={formData.currentTools}
                  onChange={handleInputChange}
                  className={styles.textarea}
                  placeholder="e.g., Blender, FreeCAD, SolidWorks, etc."
                  rows={2}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="howHeardAboutUs" className={styles.label}>
                  How did you hear about us?
                </label>
                <input
                  type="text"
                  id="howHeardAboutUs"
                  name="howHeardAboutUs"
                  value={formData.howHeardAboutUs}
                  onChange={handleInputChange}
                  className={styles.input}
                  placeholder="e.g., Google, LinkedIn, referral, etc."
                />
              </div>
              
              {/* Terms and Consent */}
              <div className={styles.checkboxSection}>
                <div className={styles.checkboxGroup}>
                  <label className={`${styles.checkboxLabel} ${errors.agreeToTerms ? styles.checkboxError : ''}`}>
                    <input
                      type="checkbox"
                      name="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={handleInputChange}
                      className={styles.checkbox}
                    />
                    <span className={styles.checkboxText}>
                      I agree to SiameseAI's{' '}
                      <a 
                        href="/terms" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={styles.link}
                      >
                        terms and conditions
                      </a>
                      <span className={styles.required}> *</span>
                    </span>
                  </label>
                  {errors.agreeToTerms && <span className={styles.errorText}>{errors.agreeToTerms}</span>}
                </div>
                
                <p className={styles.newsletterNote}>
                  I consent to receive newsletters and other communications from SiameseAI. I understand that I may withdraw this consent at any time by clicking the "unsubscribe" link included in every email.
                </p>
              </div>
              
              {/* Submit Button */}
              <button 
                type="submit"
                className={styles.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingUserInfo;

